import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, RefreshControl, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { useThemePalette } from '@/config/theme';
import { KnockoutBracket, isKnockoutRound } from '@/components/leagues/KnockoutBracket';
import { StandingsTable } from '@/components/leagues/StandingsTable';
import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { FixtureCard } from '@/components/matches/FixtureCard';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { FollowButton } from '@/components/ui/FollowButton';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useCompetition,
  useCompetitionFixtures,
  useCompetitionSeasonFixtures,
  useTeams,
  useTopPlayers,
} from '@/features/leagues/hooks';
import { useStandings } from '@/features/matches/hooks';
import { useRefresh } from '@/lib/data/useRefresh';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeCompetition, localizeTeamName } from '@/lib/i18n/footballNames';
import type { TopPlayerEntry } from '@/lib/supabase/types';

type CompetitionTab = 'standings' | 'fixtures' | 'results' | 'bracket' | 'scorers' | 'teams';

const PODIUM = ['#F4C84B', '#C7CDDA', '#CD8B53'];

function TopPlayerRow({ entry, rank }: { entry: TopPlayerEntry; rank: number }) {
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const medal = rank <= 3 ? PODIUM[rank - 1] : null;
  const teamLabel = entry.team_name ? localizeTeamName({ name: entry.team_name }, locale) : '';
  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push(`/player/${entry.player_id}`)}
      className="flex-row items-center gap-3 px-4 py-3 active:bg-surface-2/60"
    >
      {medal ? (
        <View className="h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: medal }}>
          <AppText weight="bold" tabular style={{ color: '#1A1408', fontSize: 11 }}>
            {formatNumber(rank, numeralOptions)}
          </AppText>
        </View>
      ) : (
        <AppText variant="micro" weight="bold" tabular className="w-6 text-center text-muted">
          {formatNumber(rank, numeralOptions)}
        </AppText>
      )}
      <PlayerAvatar uri={entry.photo_url} name={entry.known_as ?? entry.name} size={38} />
      <View className="flex-1 gap-0.5">
        <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
          {entry.known_as ?? entry.name}
        </AppText>
        <View className="flex-row items-center gap-1.5">
          {entry.team_logo_url ? (
            <TeamCrest uri={entry.team_logo_url} name={teamLabel} size="sm" />
          ) : null}
          <AppText variant="micro" className="text-muted" numberOfLines={1}>
            {teamLabel}
          </AppText>
        </View>
      </View>
      <View className="flex-row items-center gap-4">
        <View className="items-center">
          <AppText variant="body" weight="bold" tabular className="text-volt-bright">
            {formatNumber(entry.goals, numeralOptions)}
          </AppText>
          <AppText variant="micro" className="uppercase text-muted">
            {t('leagues.goalsShort')}
          </AppText>
        </View>
        <View className="items-center">
          <AppText variant="caption" weight="bold" tabular className="text-soft">
            {formatNumber(entry.assists, numeralOptions)}
          </AppText>
          <AppText variant="micro" className="uppercase text-muted">
            {t('leagues.assistsShort')}
          </AppText>
        </View>
      </View>
    </Pressable>
  );
}

export default function CompetitionScreen() {
  const palette = useThemePalette();
  // `name` is passed from the leagues list so the header shows the competition
  // name instantly on tap (instead of a blank skeleton while data loads).
  const { id, name } = useLocalSearchParams<{ id: string; name?: string }>();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [tab, setTab] = useState<CompetitionTab>('standings');
  const [scorerStat, setScorerStat] = useState<'goals' | 'assists'>('goals');

  const competitionQuery = useCompetition(id);
  const competition = competitionQuery.data;
  const season = competition?.current_season ?? undefined;

  const standingsQuery = useStandings(id, season);
  const upcomingQuery = useCompetitionFixtures(tab === 'fixtures' ? id : undefined, 'upcoming');
  const resultsQuery = useCompetitionFixtures(tab === 'results' ? id : undefined, 'results');
  const scorersQuery = useTopPlayers(tab === 'scorers' ? id : undefined, season, scorerStat);
  const teamsQuery = useTeams(tab === 'teams' ? id : undefined);
  const seasonFixturesQuery = useCompetitionSeasonFixtures(
    tab === 'bracket' ? id : undefined,
    season,
  );
  const knockoutFixtures = (seasonFixturesQuery.data ?? []).filter((fixture) =>
    isKnockoutRound(fixture.round),
  );

  const activeRefetch =
    tab === 'fixtures'
      ? upcomingQuery.refetch
      : tab === 'results'
        ? resultsQuery.refetch
        : tab === 'scorers'
          ? scorersQuery.refetch
          : tab === 'teams'
            ? teamsQuery.refetch
            : tab === 'bracket'
              ? seasonFixturesQuery.refetch
              : standingsQuery.refetch;
  const { refreshing, onRefresh } = useRefresh([
    () => competitionQuery.refetch(),
    () => activeRefetch(),
  ]);

  if (competitionQuery.isPending) {
    return (
      <Screen>
        <BackHeader title={name ? localizeCompetition(name, locale) : undefined} />
        <View className="gap-4 px-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-9 w-72" />
          <Skeleton className="h-64 w-full" />
        </View>
      </Screen>
    );
  }

  if (competitionQuery.isError || !competition) {
    return (
      <Screen>
        <BackHeader title={name ? localizeCompetition(name, locale) : undefined} />
        <ErrorState error={competitionQuery.error} onRetry={() => competitionQuery.refetch()} />
      </Screen>
    );
  }

  const tabs: { value: CompetitionTab; label: string }[] = [
    { value: 'standings', label: t('leagues.standings') },
    { value: 'fixtures', label: t('leagues.fixtures') },
    { value: 'results', label: t('common.finished') },
    ...(competition.type === 'cup'
      ? [{ value: 'bracket' as const, label: t('leagues.bracket') }]
      : []),
    { value: 'scorers', label: t('leagues.topScorers') },
    { value: 'teams', label: t('leagues.teams') },
  ];

  const competitionName = localizeCompetition(competition.name, locale);

  return (
    <Screen>
      <BackHeader title={competitionName} />
      <ScrollView
        contentContainerStyle={{ paddingBottom: 32 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={palette.volt}
            colors={[palette.volt]}
            progressBackgroundColor={palette.surface}
          />
        }
      >
        <Card className="mx-4 mb-4 flex-row items-center gap-4 px-4 py-4">
          {/* Light tile so DARK competition logos stay visible on the dark card. */}
          <View
            className="items-center justify-center rounded-xl"
            style={{ width: 60, height: 60, padding: 8, backgroundColor: '#EDF1F7' }}
          >
            <TeamCrest uri={competition.logo_url} name={competitionName} size="lg" />
          </View>
          <View className="flex-1 gap-1">
            <AppText variant="heading" numberOfLines={2}>
              {competitionName}
            </AppText>
            <AppText variant="micro">{competition.country ?? ''}</AppText>
            {competition.is_fantasy_enabled ? (
              <View className="mt-1 self-start rounded-full bg-volt/15 px-2.5 py-1">
                <AppText variant="micro" weight="bold" className="text-volt">
                  {t('leagues.fantasyAvailable')}
                </AppText>
              </View>
            ) : null}
          </View>
          <FollowButton entityType="competition" entityId={id} />
        </Card>

        <View className="mb-4">
          <Segmented options={tabs} value={tab} onChange={setTab} />
        </View>

        {tab === 'standings' ? (
          standingsQuery.isPending ? (
            <View className="px-4">
              <Skeleton className="h-64 w-full" />
            </View>
          ) : standingsQuery.isError ? (
            <ErrorState error={standingsQuery.error} onRetry={() => standingsQuery.refetch()} />
          ) : (standingsQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon="podium-outline"
              title={t('leagues.noStandingsTitle')}
              body={t('leagues.noStandingsBody')}
            />
          ) : (
            <View className="px-4">
              <StandingsTable rows={standingsQuery.data ?? []} />
            </View>
          )
        ) : null}

        {tab === 'fixtures' || tab === 'results' ? (
          (() => {
            const query = tab === 'fixtures' ? upcomingQuery : resultsQuery;
            if (query.isPending) {
              return (
                <View className="gap-3 px-4">
                  {[0, 1, 2].map((index) => (
                    <Skeleton key={index} className="h-16 w-full" />
                  ))}
                </View>
              );
            }
            if (query.isError) {
              return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
            }
            const fixtures = query.data ?? [];
            if (fixtures.length === 0) {
              return (
                <EmptyState
                  icon="calendar-outline"
                  title={t('leagues.offSeasonTitle')}
                  body={t('leagues.offSeasonBody')}
                  actionLabel={t('leagues.viewStandings')}
                  onAction={() => setTab('standings')}
                />
              );
            }
            return (
              <Card className="mx-4 overflow-hidden">
                {fixtures.map((fixture, index) => (
                  <View key={fixture.id} className={index > 0 ? 'border-t border-line' : ''}>
                    <FixtureCard fixture={fixture} />
                  </View>
                ))}
              </Card>
            );
          })()
        ) : null}

        {tab === 'bracket' ? (
          seasonFixturesQuery.isPending ? (
            <View className="gap-3 px-4">
              <Skeleton className="h-44 w-full" />
            </View>
          ) : seasonFixturesQuery.isError ? (
            <ErrorState
              error={seasonFixturesQuery.error}
              onRetry={() => seasonFixturesQuery.refetch()}
            />
          ) : knockoutFixtures.length === 0 ? (
            <EmptyState
              icon="git-network-outline"
              title={t('leagues.noBracketTitle')}
              body={t('leagues.noBracketBody')}
            />
          ) : (
            <KnockoutBracket fixtures={knockoutFixtures} />
          )
        ) : null}

        {tab === 'scorers' ? (
          <>
            <View className="px-4 pb-3">
              <Segmented
                options={[
                  { value: 'goals', label: t('leagues.topScorers') },
                  { value: 'assists', label: t('leagues.topAssists') },
                ]}
                value={scorerStat}
                onChange={setScorerStat}
              />
            </View>
            {scorersQuery.isPending ? (
            <View className="gap-3 px-4">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </View>
          ) : scorersQuery.isError ? (
            <ErrorState error={scorersQuery.error} onRetry={() => scorersQuery.refetch()} />
          ) : (scorersQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon="football-outline"
              title={t('player.noStatsTitle')}
              body={t('leagues.offSeasonScorers')}
              actionLabel={t('leagues.viewStandings')}
              onAction={() => setTab('standings')}
            />
          ) : (
            <Card className="mx-4 overflow-hidden">
              {(scorersQuery.data ?? []).map((entry, index) => (
                <View key={entry.player_id} className={index > 0 ? 'border-t border-line' : ''}>
                  <TopPlayerRow entry={entry} rank={index + 1} />
                </View>
              ))}
            </Card>
            )}
          </>
        ) : null}

        {tab === 'teams' ? (
          teamsQuery.isPending ? (
            <View className="gap-3 px-4">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </View>
          ) : teamsQuery.isError ? (
            <ErrorState error={teamsQuery.error} onRetry={() => teamsQuery.refetch()} />
          ) : (teamsQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon="people-outline"
              title={t('team.noPlayersTitle')}
              body={t('team.noPlayersBody')}
            />
          ) : (
            <Card className="mx-4 overflow-hidden">
              {(teamsQuery.data ?? []).map((team, index) => (
                <Pressable
                  key={team.id}
                  accessibilityRole="button"
                  onPress={() => router.push(`/team/${team.id}`)}
                  className={`flex-row items-center gap-3 px-4 py-3 active:bg-surface-2/60 ${
                    index > 0 ? 'border-t border-line' : ''
                  }`}
                >
                  <TeamCrest uri={team.logo_url} name={localizeTeamName(team, locale)} size="md" />
                  <View className="flex-1">
                    <AppText variant="caption" weight="semibold" className="text-ink">
                      {localizeTeamName(team, locale)}
                    </AppText>
                    {team.venue ? <AppText variant="micro">{team.venue}</AppText> : null}
                  </View>
                </Pressable>
              ))}
            </Card>
          )
        ) : null}
      </ScrollView>
    </Screen>
  );
}
